import { useState, useRef, useEffect, useMemo } from 'react'
import { EntitlementOption, EntitlementType } from '@/types'
import { MemberDisplayTab } from '../hooks/useClubMembers'
import { EntitlementIcon } from './ui'

// operationEntitlements: key = operation name, value = selected entitlements
// If an operation key exists with empty array, it means "any entitlement for this op"
export interface MemberFilterState {
  singlesMin: string
  singlesMax: string
  doublesMin: string
  doublesMax: string
  joinedAfter: string
  joinedBefore: string
  invitationTypes: string[]
  ratingStatus: 'all' | 'rated' | 'unrated'
  operationEntitlements: Record<string, EntitlementType[]>
}

export const EMPTY_FILTER: MemberFilterState = {
  singlesMin: '',
  singlesMax: '',
  doublesMin: '',
  doublesMax: '',
  joinedAfter: '',
  joinedBefore: '',
  invitationTypes: [],
  ratingStatus: 'all',
  operationEntitlements: {},
}

interface MemberFilterPopoverProps {
  filter: MemberFilterState
  onChange: (filter: MemberFilterState) => void
  tab: MemberDisplayTab
  isDirector: boolean
  operationOptions: string[]
  entitlementOptions: EntitlementOption[]
}

const INVITATION_TYPES = [
  { value: 'INVITATION', label: 'Web invite' },
  { value: 'INVITATION_CSV', label: 'CSV invite' },
  { value: 'PARTNER_INVITE', label: 'Partner invite' },
]

const formatOp = (op: string) => op.replace(/_/g, ' ')
const formatEnt = (e: string) =>
  e.replace(/_/g, ' ').replace(/\bL(\d)\b/g, 'L$1').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())

const ENT_CHIP_COLORS: Record<string, { active: string; inactive: string }> = {
  FREE_L1: { active: 'bg-gray-200 text-gray-800 border-gray-300', inactive: 'bg-gray-50 text-gray-500 border-gray-200' },
  PREMIUM_L1: { active: 'bg-amber-100 text-amber-800 border-amber-300', inactive: 'bg-amber-50/50 text-amber-600/70 border-amber-200' },
  PREMIUM_L2: { active: 'bg-amber-200 text-amber-900 border-amber-400', inactive: 'bg-amber-50/50 text-amber-600/70 border-amber-200' },
  VERIFIED_L1: { active: 'bg-green-100 text-green-800 border-green-300', inactive: 'bg-green-50/50 text-green-600/70 border-green-200' },
  ULTIMATE_PICKLEBALL_LEAGUE_L1: { active: 'bg-indigo-100 text-indigo-800 border-indigo-300', inactive: 'bg-indigo-50/50 text-indigo-600/70 border-indigo-200' },
  RESET_MARCH_2026: { active: 'bg-rose-100 text-rose-800 border-rose-300', inactive: 'bg-rose-50/50 text-rose-600/70 border-rose-200' },
}

export const countActiveFilters = (f: MemberFilterState): number => {
  let n = 0
  if (f.singlesMin || f.singlesMax) n++
  if (f.doublesMin || f.doublesMax) n++
  if (f.joinedAfter || f.joinedBefore) n++
  if (f.invitationTypes.length > 0) n++
  if (f.ratingStatus !== 'all') n++
  if (Object.keys(f.operationEntitlements).length > 0) n++
  return n
}

export const describeActiveFilters = (
  f: MemberFilterState,
  entitlementOptions: EntitlementOption[] = []
): string[] => {
  const labelByValue = new Map(entitlementOptions.map((o) => [o.value, o.displayName]))
  const chips: string[] = []
  if (f.singlesMin && f.singlesMax) chips.push(`Singles ${f.singlesMin}-${f.singlesMax}`)
  else if (f.singlesMin) chips.push(`Singles >= ${f.singlesMin}`)
  else if (f.singlesMax) chips.push(`Singles <= ${f.singlesMax}`)

  if (f.doublesMin && f.doublesMax) chips.push(`Doubles ${f.doublesMin}-${f.doublesMax}`)
  else if (f.doublesMin) chips.push(`Doubles >= ${f.doublesMin}`)
  else if (f.doublesMax) chips.push(`Doubles <= ${f.doublesMax}`)

  if (f.joinedAfter && f.joinedBefore) chips.push(`Joined ${f.joinedAfter} to ${f.joinedBefore}`)
  else if (f.joinedAfter) chips.push(`Joined after ${f.joinedAfter}`)
  else if (f.joinedBefore) chips.push(`Joined before ${f.joinedBefore}`)

  if (f.ratingStatus === 'rated') chips.push('Rated only')
  if (f.ratingStatus === 'unrated') chips.push('Unrated only')

  if (f.invitationTypes.length > 0) chips.push(`Invite: ${f.invitationTypes.length} type(s)`)

  for (const [op, ents] of Object.entries(f.operationEntitlements)) {
    if (ents.length > 3) {
      chips.push(`${formatOp(op)}: ${ents.length} benefits`)
    } else {
      chips.push(`${formatOp(op)}: ${ents.map((e) => labelByValue.get(e) ?? formatEnt(e)).join(', ')}`)
    }
  }

  return chips
}

const SectionHeader = ({ label }: { label: string }) => (
  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{label}</div>
)

const SmallInput = ({ value, onChange, placeholder, type = 'text' }: {
  value: string; onChange: (v: string) => void; placeholder: string; type?: string
}) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-1.5 py-1 border border-gray-200 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
  />
)

export const MemberFilterPopover = ({
  filter,
  onChange,
  tab,
  isDirector,
  operationOptions,
  entitlementOptions,
}: MemberFilterPopoverProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const activeCount = useMemo(() => countActiveFilters(filter), [filter])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const set = <K extends keyof MemberFilterState>(key: K, value: MemberFilterState[K]) => {
    onChange({ ...filter, [key]: value })
  }

  const toggleInviteType = (value: string) => {
    const arr = filter.invitationTypes
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
    onChange({ ...filter, invitationTypes: next })
  }

  // Operation chip toggle: add/remove from operationEntitlements
  // When toggling ON, pre-select all benefits so the filter sends specific keys to ES
  const toggleOperation = (op: string) => {
    const next = { ...filter.operationEntitlements }
    if (op in next) {
      delete next[op]
    } else {
      next[op] = entitlementOptions.map((o) => o.value)
    }
    onChange({ ...filter, operationEntitlements: next })
  }

  // Entitlement chip toggle within a selected operation
  const toggleEntitlementForOp = (op: string, ent: EntitlementType) => {
    const current = filter.operationEntitlements[op] ?? []
    const next = current.includes(ent) ? current.filter((e) => e !== ent) : [...current, ent]
    onChange({
      ...filter,
      operationEntitlements: { ...filter.operationEntitlements, [op]: next },
    })
  }

  const selectedOps = Object.keys(filter.operationEntitlements)
  const showInviteType = tab === 'PENDING_INVITE'
  const showEntitlements = isDirector && operationOptions.length > 0

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-2 py-1.5 border text-xs transition-colors ${
          activeCount > 0
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        Filters
        {activeCount > 0 && (
          <span className="bg-primary-600 text-white text-[9px] font-bold px-1 py-0.5 leading-none">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 shadow-lg w-72 max-h-[70vh] overflow-y-auto">
          {/* Rating range */}
          <div className="p-2 border-b border-gray-100">
            <SectionHeader label="Rating" />
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[10px] text-gray-500 w-10 flex-shrink-0">Singles</span>
              <SmallInput value={filter.singlesMin} onChange={(v) => set('singlesMin', v)} placeholder="Min" type="number" />
              <span className="text-gray-300 text-[10px]">-</span>
              <SmallInput value={filter.singlesMax} onChange={(v) => set('singlesMax', v)} placeholder="Max" type="number" />
            </div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[10px] text-gray-500 w-10 flex-shrink-0">Doubles</span>
              <SmallInput value={filter.doublesMin} onChange={(v) => set('doublesMin', v)} placeholder="Min" type="number" />
              <span className="text-gray-300 text-[10px]">-</span>
              <SmallInput value={filter.doublesMax} onChange={(v) => set('doublesMax', v)} placeholder="Max" type="number" />
            </div>
            <div className="flex gap-1">
              {(['all', 'rated', 'unrated'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => set('ratingStatus', v)}
                  className={`flex-1 text-[10px] py-0.5 border transition-colors ${
                    filter.ratingStatus === v
                      ? 'border-primary-400 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {v === 'all' ? 'All' : v === 'rated' ? 'Rated' : 'NR'}
                </button>
              ))}
            </div>
          </div>

          {/* Join date range */}
          <div className="p-2 border-b border-gray-100">
            <SectionHeader label="Joined" />
            <div className="flex items-center gap-1.5">
              <SmallInput value={filter.joinedAfter} onChange={(v) => set('joinedAfter', v)} placeholder="From" type="date" />
              <span className="text-gray-300 text-[10px]">-</span>
              <SmallInput value={filter.joinedBefore} onChange={(v) => set('joinedBefore', v)} placeholder="To" type="date" />
            </div>
          </div>

          {/* Invitation type (only on PENDING_INVITE tab) */}
          {showInviteType && (
            <div className="p-2 border-b border-gray-100">
              <SectionHeader label="Invitation Type" />
              <div className="flex flex-wrap gap-1">
                {INVITATION_TYPES.map(({ value, label }) => {
                  const active = filter.invitationTypes.includes(value)
                  return (
                    <button
                      key={value}
                      onClick={() => toggleInviteType(value)}
                      className={`px-2 py-0.5 text-[10px] border transition-colors ${
                        active
                          ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Entitlements — chip-based operation > entitlement selector */}
          {showEntitlements && (
            <div className="p-2 border-b border-gray-100">
              <SectionHeader label="Entitlements" />

              {/* Operation chips */}
              <div className="flex flex-wrap gap-1 mb-1">
                {operationOptions.map((op) => {
                  const active = op in filter.operationEntitlements
                  return (
                    <button
                      key={op}
                      onClick={() => toggleOperation(op)}
                      className={`px-2 py-0.5 text-[10px] border transition-colors ${
                        active
                          ? 'border-primary-400 bg-primary-100 text-primary-800 font-semibold'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {formatOp(op)}
                    </button>
                  )
                })}
              </div>

              {/* Per-operation entitlement chips */}
              {selectedOps.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  {selectedOps.map((op) => {
                    const selectedEnts = filter.operationEntitlements[op] ?? []
                    return (
                      <div key={op}>
                        <div className="text-[9px] text-gray-400 font-medium mb-0.5">
                          {formatOp(op)}
                          {selectedEnts.length === entitlementOptions.length && <span className="ml-1 italic">all</span>}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {entitlementOptions.map((option) => {
                            const active = selectedEnts.includes(option.value)
                            const colors = ENT_CHIP_COLORS[option.value] ?? { active: 'bg-gray-200 text-gray-800 border-gray-300', inactive: 'bg-gray-50 text-gray-400 border-gray-200' }
                            return (
                              <button
                                key={option.value}
                                onClick={() => toggleEntitlementForOp(op, option.value)}
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] border transition-colors ${
                                  active ? colors.active + ' font-semibold' : colors.inactive + ' hover:opacity-80'
                                }`}
                              >
                                <EntitlementIcon entitlement={option.value} size="xs" />
                                {option.displayName}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {selectedOps.length === 0 && (
                <div className="text-[10px] text-gray-400 italic mt-0.5">Select an operation to filter by entitlement</div>
              )}
            </div>
          )}

          {/* Clear all */}
          {activeCount > 0 && (
            <div className="p-1.5">
              <button
                onClick={() => onChange(EMPTY_FILTER)}
                className="w-full text-center text-[11px] text-gray-500 hover:text-gray-700 py-0.5"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
